import { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider,
  Grid, FormControlLabel, Switch, Alert, IconButton, Chip, LinearProgress,
  Autocomplete, InputAdornment, Tooltip,
} from '@mui/material';
import { AutoFixHigh, Close } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { listZones, type OperationalZoneRow } from '../../services/zoneService';
import {
  PARTNER_PLAN_FORM_TABS,
  PARTNER_PLAN_FEATURE_SUGGESTIONS,
  PARTNER_PLAN_SELECTABLE_TYPES,
  PARTNER_PLAN_NOTE_SX,
  PARTNER_PLAN_ALERT_SX,
  PARTNER_PLAN_FORM_HELPER_SX,
  PARTNER_PLAN_GRACE_PERIOD_MAX,
  PARTNER_PLAN_GRACE_PERIOD_MIN,
  PARTNER_PLAN_MAX_RIDES_MAX,
  PARTNER_PLAN_MAX_RIDES_MIN,
  PARTNER_PLAN_PRICE_MAX,
  PARTNER_PLAN_PRICE_MIN,
  PARTNER_PLAN_TRIAL_DAYS_MAX,
  PARTNER_PLAN_TRIAL_DAYS_MIN,
  PARTNER_PLAN_VALIDITY_DAYS_MAX,
  PARTNER_PLAN_VALIDITY_DAYS_MIN,
  VALIDITY_BY_INTERVAL,
  type PartnerPlanBillingInterval,
} from '../../constants/partnerSubscriptionPlanConfig';
import {
  BILLING_INTERVAL_OPTIONS,
  PLAN_CURRENCY_OPTIONS,
  emptyPartnerPlanForm,
  planRowToForm,
  validatePartnerPlanForm,
  buildPartnerPlanPayload,
  slugifyPartnerPlanCode,
  validatePartnerPlanDescription,
  resolvePartnerTypeFields,
  type PartnerSubscriptionPlanPayload,
  type PartnerSubscriptionPlanRow,
} from '../../services/partnerSubscriptionPlanService';
import PersonNameField from '../ui/forms/PersonNameField';
import { sanitizePersonName } from '../../utils/personFieldValidation';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  plan?: PartnerSubscriptionPlanRow | null;
  loading?: boolean;
  error?: string;
  defaultZoneId?: string;
  onClose: () => void;
  onSubmit: (payload: PartnerSubscriptionPlanPayload) => void;
}

const TAB_IDS = PARTNER_PLAN_FORM_TABS.map((t) => t.id);

export default function PartnerSubscriptionPlanFormDrawer({
  open, mode, plan, loading, error, defaultZoneId, onClose, onSubmit,
}: Props) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState<PartnerSubscriptionPlanPayload>(emptyPartnerPlanForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [autoCode, setAutoCode] = useState(true);

  const { data: zonesData } = useQuery({
    queryKey: ['zones', 'all-for-partner-plans'],
    queryFn: () => listZones({ limit: 200 }),
    enabled: open,
  });
  const zones = zonesData?.rows || [];

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setErrors({});
    setAutoCode(mode === 'create');
    setForm(plan && mode === 'edit' ? planRowToForm(plan) : emptyPartnerPlanForm(
      defaultZoneId ? { zoneIds: [defaultZoneId] } : undefined,
    ));
  }, [open, mode, plan, defaultZoneId]);

  useEffect(() => {
    if (mode !== 'create' || !autoCode) return;
    const next = slugifyPartnerPlanCode(form.name);
    setForm((f) => (f.code === next ? f : { ...f, code: next }));
  }, [form.name, autoCode, mode]);

  const selectedZones = useMemo(
    () => zones.filter((z) => form.zoneIds.includes(z._id)),
    [zones, form.zoneIds],
  );
  const selectedPartnerTypes = useMemo(
    () => PARTNER_PLAN_SELECTABLE_TYPES.filter((o) => (form.partnerTypes || []).includes(o.value)),
    [form.partnerTypes],
  );
  const zonesLocked = !!defaultZoneId && mode === 'create';

  const patch = (p: Partial<PartnerSubscriptionPlanPayload>) => setForm((f) => ({ ...f, ...p }));
  const clearFieldError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[field];
      return nextErrors;
    });
  };
  const clampInt = (value: number, min: number, max: number) => {
    if (!Number.isFinite(value)) return min;
    return Math.min(Math.max(Math.trunc(value), min), max);
  };
  const activeTabId = TAB_IDS[tab];
  const tabMeta = PARTNER_PLAN_FORM_TABS[tab];
  const canSave = useMemo(() => Object.keys(validatePartnerPlanForm(form)).length === 0, [form]);

  const goNext = () => {
    const tabErrors = validatePartnerPlanForm(form, activeTabId);
    if (Object.keys(tabErrors).length) {
      setErrors(tabErrors);
      return;
    }
    setErrors({});
    setTab((t) => Math.min(t + 1, TAB_IDS.length - 1));
  };

  const handleSave = () => {
    for (let i = 0; i < TAB_IDS.length; i += 1) {
      const tabErrors = validatePartnerPlanForm(form, TAB_IDS[i]);
      if (Object.keys(tabErrors).length) {
        setErrors(tabErrors);
        setTab(i);
        return;
      }
    }
    onSubmit(buildPartnerPlanPayload(form));
  };

  const progress = ((tab + 1) / TAB_IDS.length) * 100;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 540 } } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {mode === 'create' ? 'New partner subscription plan' : 'Edit partner subscription plan'}
            </Typography>
            <Typography variant="body2" sx={PARTNER_PLAN_NOTE_SX}>
              Tenant-scoped plan for drivers/partners · Step {tab + 1} of {TAB_IDS.length} · {tabMeta.subtitle}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Stack>

        <LinearProgress variant="determinate" value={progress} sx={{ height: 3 }} />

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5, ...PARTNER_PLAN_FORM_HELPER_SX }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {activeTabId === 'info' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Plan code"
                  value={form.code}
                  disabled={mode === 'edit'}
                  error={!!errors.code}
                  helperText={
                    errors.code
                    || (mode === 'edit'
                      ? 'Code cannot be changed after create'
                      : (autoCode
                        ? 'Auto-filled from plan name — type manually or use the icon'
                        : 'Unique within your tenant, e.g. weekly_basic'))
                  }
                  onChange={(e) => {
                    setAutoCode(false);
                    patch({ code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') });
                  }}
                  InputProps={mode === 'create' ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <Tooltip title="Auto-fill code from plan name">
                            <span>
                              <IconButton
                                size="small"
                                edge="end"
                                aria-label="Auto-fill plan code from plan name"
                                disabled={!form.name.trim()}
                                onClick={() => {
                                  setAutoCode(true);
                                  patch({ code: slugifyPartnerPlanCode(form.name) });
                                }}
                              >
                                <AutoFixHigh fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <FormControlLabel
                            sx={{ mr: 0, ml: 0.25 }}
                            control={(
                              <Switch
                                size="small"
                                checked={autoCode}
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  setAutoCode(on);
                                  if (on) patch({ code: slugifyPartnerPlanCode(form.name) });
                                }}
                              />
                            )}
                            label={<Typography variant="caption" fontWeight={700}>Auto</Typography>}
                          />
                        </Stack>
                      </InputAdornment>
                    ),
                  } : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PersonNameField
                  label="Plan name"
                  required
                  fullWidth
                  size="small"
                  value={form.name}
                  error={!!errors.name}
                  helperText={errors.name || 'Letters only — no numbers or special characters'}
                  inputProps={{ maxLength: 80 }}
                  onChange={(e) => {
                    patch({ name: sanitizePersonName(e.target.value) });
                    if (errors.name) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.name;
                        return next;
                      });
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Description"
                  value={form.description || ''}
                  placeholder="Shown to drivers during mobile registration"
                  error={!!errors.description}
                  helperText={errors.description || 'Avoid repeating the same word consecutively'}
                  onChange={(e) => {
                    const description = e.target.value;
                    patch({ description });
                    const descErr = validatePartnerPlanDescription(description);
                    setErrors((prev) => {
                      if (descErr) return { ...prev, description: descErr };
                      if (!prev.description) return prev;
                      const next = { ...prev };
                      delete next.description;
                      return next;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={zones}
                  value={selectedZones}
                  disabled={zonesLocked}
                  getOptionLabel={(z) => `${z.name} (${z.code})`}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  onChange={(_, value: OperationalZoneRow[]) => patch({ zoneIds: value.map((z) => z._id) })}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option._id}
                        label={option.name ? `${option.name} (${option.code})` : option.code}
                        size="small"
                        sx={{ height: 24, '& .MuiChip-label': { lineHeight: 1.25 } }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      required
                      label="Zones"
                      error={!!errors.zoneIds}
                      helperText={
                        errors.zoneIds
                        || (zonesLocked
                          ? 'Plan is scoped to this zone from zone management'
                          : 'Select one or more zones — drivers in these zones see this plan')
                      }
                      sx={{
                        '& .MuiInputBase-root': {
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          py: 0.75,
                        },
                        '& .MuiAutocomplete-tag': { m: 0 },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={PARTNER_PLAN_SELECTABLE_TYPES}
                  value={selectedPartnerTypes}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  onChange={(_, value) => patch(resolvePartnerTypeFields(value.map((v) => v.value)))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        size="small"
                        sx={{ height: 24, '& .MuiChip-label': { lineHeight: 1.25 } }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      required
                      label="Partner types"
                      error={!!errors.partnerTypes}
                      helperText={
                        errors.partnerTypes
                        || 'Select one or more partner types that share this price'
                      }
                      sx={{
                        '& .MuiInputBase-root': {
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          py: 0.75,
                        },
                        '& .MuiAutocomplete-tag': { m: 0 },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="number" label="Sort order" value={form.sortOrder}
                  onChange={(e) => patch({ sortOrder: Number(e.target.value) })} />
              </Grid>
            </Grid>
          )}

          {activeTabId === 'pricing' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Price"
                  value={form.price}
                  error={!!errors.price}
                  helperText={
                    errors.price
                    || `0 for free · max ${PARTNER_PLAN_PRICE_MAX.toLocaleString('en-IN')}`
                  }
                  inputProps={{
                    min: PARTNER_PLAN_PRICE_MIN,
                    max: PARTNER_PLAN_PRICE_MAX,
                    step: 1,
                  }}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = raw === '' ? 0 : Number(raw);
                    patch({
                      price: Number.isFinite(next)
                        ? Math.min(Math.max(next, PARTNER_PLAN_PRICE_MIN), PARTNER_PLAN_PRICE_MAX)
                        : PARTNER_PLAN_PRICE_MIN,
                    });
                    clearFieldError('price');
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" select label="Currency" value={form.currency}
                  onChange={(e) => patch({ currency: e.target.value })}>
                  {PLAN_CURRENCY_OPTIONS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" select label="Billing cycle" value={form.billingInterval}
                  onChange={(e) => {
                    const billingInterval = e.target.value as PartnerPlanBillingInterval;
                    patch({
                      billingInterval,
                      validityDays: VALIDITY_BY_INTERVAL[billingInterval] || form.validityDays,
                    });
                  }}>
                  {BILLING_INTERVAL_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Validity (days)"
                  value={form.validityDays}
                  error={!!errors.validityDays}
                  helperText={
                    errors.validityDays
                    || `Min ${PARTNER_PLAN_VALIDITY_DAYS_MIN} · max ${PARTNER_PLAN_VALIDITY_DAYS_MAX} days`
                  }
                  inputProps={{
                    min: PARTNER_PLAN_VALIDITY_DAYS_MIN,
                    max: PARTNER_PLAN_VALIDITY_DAYS_MAX,
                    step: 1,
                  }}
                  onChange={(e) => {
                    const next = e.target.value === '' ? PARTNER_PLAN_VALIDITY_DAYS_MIN : Number(e.target.value);
                    patch({ validityDays: clampInt(next, PARTNER_PLAN_VALIDITY_DAYS_MIN, PARTNER_PLAN_VALIDITY_DAYS_MAX) });
                    clearFieldError('validityDays');
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Trial days"
                  value={form.trialDays}
                  error={!!errors.trialDays}
                  helperText={
                    errors.trialDays
                    || `0 = no trial · max ${PARTNER_PLAN_TRIAL_DAYS_MAX} days`
                  }
                  inputProps={{
                    min: PARTNER_PLAN_TRIAL_DAYS_MIN,
                    max: PARTNER_PLAN_TRIAL_DAYS_MAX,
                    step: 1,
                  }}
                  onChange={(e) => {
                    const next = e.target.value === '' ? 0 : Number(e.target.value);
                    patch({ trialDays: clampInt(next, PARTNER_PLAN_TRIAL_DAYS_MIN, PARTNER_PLAN_TRIAL_DAYS_MAX) });
                    clearFieldError('trialDays');
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Grace period (days)"
                  value={form.gracePeriodDays}
                  error={!!errors.gracePeriodDays}
                  helperText={
                    errors.gracePeriodDays
                    || `Min ${PARTNER_PLAN_GRACE_PERIOD_MIN} · max ${PARTNER_PLAN_GRACE_PERIOD_MAX} days after expiry`
                  }
                  inputProps={{
                    min: PARTNER_PLAN_GRACE_PERIOD_MIN,
                    max: PARTNER_PLAN_GRACE_PERIOD_MAX,
                    step: 1,
                  }}
                  onChange={(e) => {
                    const next = e.target.value === '' ? 0 : Number(e.target.value);
                    patch({ gracePeriodDays: clampInt(next, PARTNER_PLAN_GRACE_PERIOD_MIN, PARTNER_PLAN_GRACE_PERIOD_MAX) });
                    clearFieldError('gracePeriodDays');
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Max rides per period"
                  value={form.maxRidesPerPeriod ?? ''}
                  error={!!errors.maxRidesPerPeriod}
                  helperText={
                    errors.maxRidesPerPeriod
                    || `Blank = unlimited · max ${PARTNER_PLAN_MAX_RIDES_MAX.toLocaleString('en-IN')}`
                  }
                  inputProps={{
                    min: PARTNER_PLAN_MAX_RIDES_MIN,
                    max: PARTNER_PLAN_MAX_RIDES_MAX,
                    step: 1,
                  }}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patch({ maxRidesPerPeriod: undefined });
                      clearFieldError('maxRidesPerPeriod');
                      return;
                    }
                    const next = Number(raw);
                    patch({
                      maxRidesPerPeriod: Number.isFinite(next)
                        ? clampInt(next, PARTNER_PLAN_MAX_RIDES_MIN, PARTNER_PLAN_MAX_RIDES_MAX)
                        : PARTNER_PLAN_MAX_RIDES_MIN,
                    });
                    clearFieldError('maxRidesPerPeriod');
                  }}
                />
              </Grid>
            </Grid>
          )}

          {activeTabId === 'benefits' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Plan rules</Typography>
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.commissionWaived}
                          onChange={(e) => patch({ commissionWaived: e.target.checked })}
                        />
                      }
                      label="Waive per-ride commission while active"
                    />
                    <Typography variant="body2" sx={{ ...PARTNER_PLAN_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                      {form.commissionWaived
                        ? 'Partners on this plan will not pay per-ride commission while the subscription is active.'
                        : 'Standard per-ride commission will apply on every completed ride.'}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.autoRenew}
                          onChange={(e) => patch({ autoRenew: e.target.checked })}
                        />
                      }
                      label="Auto-renew at end of billing cycle"
                    />
                    <Typography variant="body2" sx={{ ...PARTNER_PLAN_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                      {form.autoRenew
                        ? 'The plan will renew automatically at the end of each billing period unless the partner cancels.'
                        : 'Partners must manually renew when the current billing period ends.'}
                    </Typography>
                  </Box>
                </Stack>
                {!form.commissionWaived && (
                  <Alert severity="info" sx={{ mb: 2, ...PARTNER_PLAN_ALERT_SX }}>
                    Commission waiver is off — standard zone commission rates will be charged per ride.
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Quick-add features</Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
                  {PARTNER_PLAN_FEATURE_SUGGESTIONS.map((feature) => {
                    const selected = form.features.includes(feature);
                    return (
                      <Chip key={feature} label={feature} size="small" clickable
                        color={selected ? 'primary' : 'default'} variant={selected ? 'filled' : 'outlined'}
                        onClick={() => patch({
                          features: selected
                            ? form.features.filter((f) => f !== feature)
                            : [...form.features, feature],
                        })} />
                    );
                  })}
                </Stack>
                <TextField fullWidth size="small" multiline minRows={4} label="Plan features"
                  placeholder={'One benefit per line\ne.g. No commission on rides\nPriority dispatch'}
                  value={form.features.join('\n')}
                  onChange={(e) => patch({ features: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Publish & visibility</Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isFeatured}
                          onChange={(e) => patch({ isFeatured: e.target.checked })}
                        />
                      }
                      label="Featured plan"
                    />
                    <Typography variant="body2" sx={{ ...PARTNER_PLAN_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                      {form.isFeatured
                        ? 'Highlights this plan in the mobile app — add a badge label below.'
                        : 'Plan appears in the standard list without a featured badge.'}
                    </Typography>
                    {form.isFeatured && (
                      <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 1.5, ml: 4.5, width: 'calc(100% - 36px)' }}
                        label="Badge label"
                        required
                        value={form.badgeLabel || ''}
                        error={!!errors.badgeLabel}
                        placeholder="Popular, Best value"
                        helperText={errors.badgeLabel || 'Shown on the plan card during mobile registration'}
                        onChange={(e) => {
                          patch({ badgeLabel: e.target.value });
                          if (errors.badgeLabel) clearFieldError('badgeLabel');
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isActive}
                          onChange={(e) => patch({ isActive: e.target.checked })}
                        />
                      }
                      label="Active — available for purchase"
                    />
                    <Typography variant="body2" sx={{ ...PARTNER_PLAN_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                      {form.isActive
                        ? 'Partners can subscribe to this plan. Existing subscribers keep access if you later deactivate.'
                        : 'Plan is hidden from new purchases. Turn on when you are ready to offer it.'}
                    </Typography>
                    {!form.isActive && (
                      <Alert severity="warning" sx={{ mt: 1.5, ml: 4.5, width: 'calc(100% - 36px)', ...PARTNER_PLAN_ALERT_SX }}>
                        Inactive plans are not available for new subscriptions.
                      </Alert>
                    )}
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isPublic}
                          onChange={(e) => patch({ isPublic: e.target.checked })}
                        />
                      }
                      label="Show during driver mobile registration"
                    />
                    <Typography variant="body2" sx={{ ...PARTNER_PLAN_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                      {form.isPublic
                        ? `Visible to ${selectedPartnerTypes.length ? selectedPartnerTypes.map((t) => t.label).join(', ') : 'selected partner types'} in ${selectedZones.length ? selectedZones.map((z) => z.name).join(', ') : 'selected zones'} during signup.`
                        : 'Plan stays in admin only — partners will not see it during mobile registration.'}
                    </Typography>
                    {!form.isPublic && (
                      <Alert severity="info" sx={{ mt: 1.5, ml: 4.5, width: 'calc(100% - 36px)', ...PARTNER_PLAN_ALERT_SX }}>
                        Turn on to expose this plan on the driver mobile registration screen.
                      </Alert>
                    )}
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          )}
        </Box>

        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
          {tab > 0 ? (
            <Button onClick={() => { setTab((t) => t - 1); setErrors({}); }}>Previous</Button>
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose}>Cancel</Button>
            {tab < TAB_IDS.length - 1 ? (
              <Button variant="contained" onClick={goNext}>Next</Button>
            ) : (
              <Button variant="contained" color="success" disabled={!canSave || loading} onClick={handleSave}>
                {loading ? 'Saving…' : mode === 'create' ? 'Create plan' : 'Save changes'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
